import { useEffect, useState } from 'react';
import type { ProfileDto } from '@jordan/types';
import {
  Alert,
  Avatar,
  Breadcrumb,
  Button,
  ImageUpload,
  Input,
  PageHeader,
  SelectMenu,
  SettingsPanel,
  SettingsRow,
  Spinner,
} from '@jordan/ui';
import { AppChrome } from '../components/AppChrome';
import { MediaPicker } from '../components/MediaPicker';
import { useRefreshMe } from '../auth/MeProvider';
import { apiDelete, apiGet, apiPatch, apiPut, assetUrl } from '../lib/api';
import { TIMEZONES } from '../openslot/helpers';

type Editable = Pick<
  ProfileDto,
  'displayName' | 'phone' | 'company' | 'jobTitle' | 'addressLine1' | 'addressLine2' | 'city' | 'region' | 'postalCode' | 'country' | 'timezone'
>;

const FIELD = (v: string | null | undefined) => v ?? '';

export function ProfilePage() {
  const refreshMe = useRefreshMe();
  const [profile, setProfile] = useState<ProfileDto | null>(null);
  const [f, setF] = useState<Editable | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const hydrate = (p: ProfileDto) => {
    setProfile(p);
    setF({
      displayName: FIELD(p.displayName),
      phone: FIELD(p.phone),
      company: FIELD(p.company),
      jobTitle: FIELD(p.jobTitle),
      addressLine1: FIELD(p.addressLine1),
      addressLine2: FIELD(p.addressLine2),
      city: FIELD(p.city),
      region: FIELD(p.region),
      postalCode: FIELD(p.postalCode),
      country: FIELD(p.country),
      timezone: p.timezone,
    });
  };

  useEffect(() => {
    apiGet<ProfileDto>('/api/me/profile').then(hydrate).catch(() => setError('Failed to load your profile.'));
  }, []);

  const set = <K extends keyof Editable>(k: K, v: Editable[K]) => setF((p) => (p ? { ...p, [k]: v } : p));

  const run = async (fn: () => Promise<ProfileDto>, ok: string) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      hydrate(await fn());
      await refreshMe();
      setMessage(ok);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = () => run(() => apiPatch<ProfileDto>('/api/me/profile', f), 'Profile saved.');
  const uploadAvatar = (file: File, dataUrl: string) =>
    run(() => apiPut<ProfileDto>('/api/me/avatar', { dataUrl, label: file.name }), 'Avatar updated.');
  const selectAvatar = (mediaId: string) => {
    setPickerOpen(false);
    void run(() => apiPut<ProfileDto>('/api/me/avatar', { mediaId }), 'Avatar updated.');
  };
  const removeAvatar = () => run(() => apiDelete<ProfileDto>('/api/me/avatar'), 'Avatar removed.');

  const initials = (f?.displayName || profile?.email || '?').slice(0, 2);

  return (
    <AppChrome>
      <PageHeader
        title={<>Account - <span className="fw-normal">My profile</span></>}
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Account' }, { label: 'My profile' }]} />}
      />
      <div className="content">
        {message && <Alert variant="success" icon="ph-check-circle" dismissible>{message}</Alert>}
        {error && <Alert variant="danger" icon="ph-warning" dismissible>{error}</Alert>}

        {!f || !profile ? (
          <Spinner />
        ) : (
          <>
            <SettingsPanel title="Avatar" description="Your photo, shown in the header and across the app.">
              <SettingsRow title="Profile photo" description="Upload an image or choose one from the Media Library. PNG, JPG, SVG or WebP.">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <Avatar size={48} src={assetUrl(profile.photoUrl)} initials={initials} />
                  <div className="d-flex flex-wrap gap-2">
                    <Button variant="light" icon="ph-images" onClick={() => setPickerOpen(true)}>
                      Choose from library
                    </Button>
                    {profile.photoUrl && (
                      <Button variant="light" className="text-danger" icon="ph-trash" disabled={busy} onClick={removeAvatar}>
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                <ImageUpload
                  previewSrc={null}
                  onSelect={(file, dataUrl) => void uploadAvatar(file, dataUrl)}
                  hint="Uploading also adds the image to the Media Library."
                />
              </SettingsRow>
            </SettingsPanel>

            <SettingsPanel title="Personal information" description="Your name and contact details.">
              <SettingsRow title="Name" description="Shown in the header and on bookings.">
                <Input value={f.displayName ?? ''} onChange={(e) => set('displayName', e.target.value)} />
              </SettingsRow>
              <SettingsRow title="Email" description="From your sign-in account; not editable here.">
                <Input value={profile.email} disabled readOnly />
              </SettingsRow>
              <SettingsRow title="Phone" description="Optional contact number.">
                <Input value={f.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
              </SettingsRow>
              <SettingsRow title="Company" description="Organization you represent.">
                <Input value={f.company ?? ''} onChange={(e) => set('company', e.target.value)} />
              </SettingsRow>
              <SettingsRow title="Job title" description="Your role or title.">
                <Input value={f.jobTitle ?? ''} onChange={(e) => set('jobTitle', e.target.value)} />
              </SettingsRow>
            </SettingsPanel>

            <SettingsPanel title="Address" description="Your mailing address.">
              <SettingsRow title="Address line 1">
                <Input value={f.addressLine1 ?? ''} onChange={(e) => set('addressLine1', e.target.value)} />
              </SettingsRow>
              <SettingsRow title="Address line 2">
                <Input value={f.addressLine2 ?? ''} onChange={(e) => set('addressLine2', e.target.value)} />
              </SettingsRow>
              <SettingsRow title="City / State / Postal code">
                <div className="row g-2">
                  <div className="col-sm-5"><Input placeholder="City" value={f.city ?? ''} onChange={(e) => set('city', e.target.value)} /></div>
                  <div className="col-sm-4"><Input placeholder="State / region" value={f.region ?? ''} onChange={(e) => set('region', e.target.value)} /></div>
                  <div className="col-sm-3"><Input placeholder="Postal code" value={f.postalCode ?? ''} onChange={(e) => set('postalCode', e.target.value)} /></div>
                </div>
              </SettingsRow>
              <SettingsRow title="Country">
                <Input value={f.country ?? ''} onChange={(e) => set('country', e.target.value)} />
              </SettingsRow>
            </SettingsPanel>

            <SettingsPanel title="Preferences" description="Personal settings.">
              <SettingsRow title="Timezone" description="Your personal timezone.">
                <SelectMenu
                  icon="ph-globe"
                  value={f.timezone}
                  onChange={(v) => set('timezone', v)}
                  options={TIMEZONES.map((t) => ({ value: t, label: t }))}
                />
              </SettingsRow>
            </SettingsPanel>

            <div className="d-flex justify-content-end mb-3">
              <Button variant="primary" disabled={busy} onClick={saveProfile}>Save changes</Button>
            </div>
          </>
        )}
      </div>

      <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(img) => selectAvatar(img.id)} />
    </AppChrome>
  );
}
